/*
 Copyright (c), CKSource Holding sp. z o.o. All rights reserved.
 */

import { EmitterMixin, type Emitter } from 'ckeditor5/src/utils.js';

import ChannelMessage from './../websocketgateway/messages/channelmessage';

import MessagesCompressor from './../messagescompressor';
import type { ISocket, IWebSocketGateway } from './websocketgateway';

/**
 * Abstracts Socket.io channels event generation as general Emitter that will fire events for all service events.
 *
 * Fires two events for each channel message:
 * 1. 'all:event:event-name'
 * 2. 'event:event-name'
 *
 * Event without 'all' prefix is not fired for messages that socketIdated from this WebSocket.sessionId.
 */
// eslint-disable-next-line new-cap
class Channel extends /* #__PURE__ -- @preserve */ EmitterMixin() implements Emitter {
	public constructor(
		private readonly _channelName: string,
		private readonly _wsGateway: IWebSocketGateway,
		private readonly _socket: ISocket
	) {
		super();

		this._subscribeToChannel();
	}

	/**
	 * Removes handlers from socket.
	 */
	public remove(): void {
		this._socket.off( this._channelName );
	}

	/**
	 * Returns normalized event name.
	 */
	public getEventName( eventName?: string | number, all: boolean = false ): string {
		let fullEventName: string = '';

		if ( all ) {
			fullEventName += 'all:';
		}

		fullEventName += 'event';

		if ( eventName ) {
			fullEventName += `:${ eventName }`;
		}

		return fullEventName;
	}

	private _subscribeToChannel(): void {
		this._socket.on( this._channelName, ( message: Buffer ) => {
			const channelMessage: ChannelMessage = MessagesCompressor.decode( message, ChannelMessage ) as ChannelMessage;

			// Allow listening to all events - including own
			this.fire( this.getEventName( channelMessage.type, true ), channelMessage.data, channelMessage.socketId );

			if ( channelMessage.socketId !== this._wsGateway.socketId ) {
				this.fire( this.getEventName( channelMessage.type ), channelMessage.data, channelMessage.socketId );
			}
		} );
	}
}

export default Channel;
